import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { IOrganizationAccess } from "src/app/shared/interfaces/access.interface";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { ApiService } from "src/app/shared/services/api.service";
import { PermissionService } from "src/app/shared/services/permission.service";
import {
  IExposedOrganizationOverview,
  IExposedOrganizationSummary,
  IOrganizationCreationDetails,
  IOrganizationDetails,
} from "../interfaces/organization.interface";

@Injectable({
  providedIn: "root",
})
export class OrganizationService {
  constructor(
    private api: ApiService,
    private permissionService: PermissionService
  ) {}

  /**
   * Create a new organization
   * @param details Organization details
   * @return The id of the newly created organization
   */
  public createOrganization(details: IOrganizationCreationDetails): Observable<void> {
    return this.api.post("organizations", details) as Observable<void>;
  }

  /**
   * Recover the full details (basic info, members, invitations) of the current organization
   * @returns The current organization's details
   */
  public getOrganizationDetails(): Observable<IOrganizationDetails> {
    return this.api.get("organizations/details") as Observable<IOrganizationDetails>;
  }

  /**
   * Recover all organizations the current user belongs to
   * @return List of organizations the current user belongs to
   */
  public getMyOrganizations(): Observable<IExposedOrganizationSummary[]> {
    return this.api.get("organizations") as Observable<IExposedOrganizationSummary[]>;
  }

  /**
   * Whether the user's role allows recovering every organization's overview (see
   * getAllOrganizations()), as a SuperAdmin does.
   * @return True if listing every organization is allowed, false otherwise
   */
  public canGetAllOrganizations(): boolean {
    return this.permissionService.isRouteEnabled(Functionality.VIEW_ORGANIZATIONS, "GET", "/organizations/all");
  }

  /**
   * Recover an overview (basic info and user count) of every organization. Only a SuperAdmin can
   * perform this action.
   * @returns List of organization overviews
   */
  public getAllOrganizations(): Observable<IExposedOrganizationOverview[]> {
    return this.api.get("organizations/all") as Observable<IExposedOrganizationOverview[]>;
  }

  /**
   * Whether the user's role allows recovering any organization's details by its id (see
   * getOrganizationDetailsById()).
   * @return True if opening an organization's details is allowed, false otherwise
   */
  public canGetOrganizationDetailsById(): boolean {
    return this.permissionService.isRouteEnabled(Functionality.VIEW_ORGANIZATIONS, "GET", "/organizations/id");
  }

  /**
   * Recover the full details (basic info, members, invitations) of a given organization. Only a
   * SuperAdmin can perform this action.
   * @param id ID of the organization to recover details for
   * @returns The organization's details
   */
  public getOrganizationDetailsById(id: number): Observable<IOrganizationDetails> {
    return this.api.get(`organizations/${encodeURIComponent(id)}`) as Observable<IOrganizationDetails>;
  }

  /**
   * Switch its active organization, provided it belongs to it
   * @param id new organization identifier
   * @return Access and refresh tokens linked to the new organization, along with the
   * functionalities and routes the user's role grants access to
   */
  public changeMyOrganization(id: number): Observable<IOrganizationAccess> {
    return this.api.post(`user/change-organization/${encodeURIComponent(id)}`, undefined) as Observable<IOrganizationAccess>;
  }

  /**
   * Remove a member from the current organization. Their account is kept, but they lose access to
   * the organization immediately.
   * @param membershipId ID of the membership to remove (`id` of the organization details' members)
   */
  public removeMember(membershipId: number): Observable<void> {
    return this.api.delete(`organizations/members/${encodeURIComponent(membershipId)}`) as Observable<void>;
  }

  /**
   * Change the role of a member of the current organization, effective on their next request.
   * @param membershipId ID of the membership to update (`id` of the organization details' members)
   * @param role Name of the member's new role
   */
  public changeMemberRole(membershipId: number, role: string): Observable<void> {
    return this.api.patch(`organizations/members/${encodeURIComponent(membershipId)}/role`, undefined, true, { role }) as Observable<void>;
  }
}
